import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  ArrowDownLeft,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { useDueCollections } from '../../services/httpServices/queries';
import { Pagination } from '../../components/ui/Pagination';
import type {
  DueCollectionEntry,
  DueCollectionSplit,
  DueCollectionsSummary,
} from '../../types/billing';

const CURRENCY = '৳';
const LIMIT = 20;

const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  insurance: 'Insurance',
  other: 'Other',
};

const SETTING_LABELS: Record<string, string> = {
  opd: 'OPD',
  ipd: 'IPD',
  pharmacy: 'Pharmacy',
};

const SOURCE_LABELS: Record<string, string> = {
  invoice: 'Invoice',
  diagnostic: 'Diagnostic',
  pharmacy: 'Pharmacy sale',
};

function fmt(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function SplitRows({
  split,
  emptyLabel,
}: {
  split?: DueCollectionSplit;
  emptyLabel: string;
}) {
  const rows = [
    { label: 'IPD', value: split?.ipd },
    { label: 'OPD', value: split?.opd },
    { label: 'Pharmacy', value: split?.pharmacy },
  ];
  return (
    <dl className="divide-y divide-border/60">
      {rows.map((row) => (
        <div key={row.label} className="flex items-center justify-between gap-3 px-3 py-1.5">
          <dt className="text-[0.6875rem] text-text-muted">{row.label}</dt>
          <dd className="text-[0.6875rem] font-medium tabular-nums text-text">{fmt(row.value)}</dd>
        </div>
      ))}
      <div className="flex items-center justify-between gap-3 bg-primary/5 px-3 py-2">
        <dt className="text-[0.6875rem] font-semibold text-text">{emptyLabel}</dt>
        <dd className="text-sm font-bold tabular-nums text-primary">{fmt(split?.total)}</dd>
      </div>
    </dl>
  );
}

export function DueCollectionsModal({
  periodLabel,
  dateFrom,
  dateTo,
  currentLabel,
  outstandingDue,
  onClose,
}: {
  periodLabel: string;
  dateFrom?: string;
  dateTo?: string;
  /** "Today Due" when the period is one day, otherwise "Current-period due". */
  currentLabel: string;
  /** All-time patient dues, shown next to the filtered collection total. */
  outstandingDue?: number;
  onClose: () => void;
}) {
  const [bucket, setBucket] = useState<'all' | 'current' | 'old'>('all');
  const [setting, setSetting] = useState<'all' | 'opd' | 'ipd' | 'pharmacy'>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Reset to the first page whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [bucket, setting, search, dateFrom, dateTo]);

  const { data, isLoading, isFetching, error } = useDueCollections(
    {
      dateFrom,
      dateTo,
      bucket: bucket === 'all' ? undefined : bucket,
      setting: setting === 'all' ? undefined : setting,
      search: search.trim() || undefined,
      page,
      limit: LIMIT,
    },
    { enabled: true },
  );

  const result = data?.data;
  const summary: DueCollectionsSummary | undefined = result?.summary;
  const entries: DueCollectionEntry[] = result?.entries || [];
  const meta = result?.meta;
  const totalPages = meta ? Math.max(1, Math.ceil(meta.total / LIMIT)) : 1;

  const currentBucketLabel = currentLabel;
  const oldBucketLabel = 'Old Due';

  const bucketOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'All' },
      { value: 'current' as const, label: currentBucketLabel },
      { value: 'old' as const, label: oldBucketLabel },
    ],
    [currentBucketLabel],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const modal = (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Due collected"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border p-4 sm:p-5">
          <div className="flex min-w-0 items-start gap-3">
            <div className="shrink-0 rounded-lg bg-primary/10 p-2">
              <ArrowDownLeft size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text">Due Collected</h2>
              <p className="text-xs text-text-muted">
                {periodLabel} · money received against bills already raised
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text"
          >
            <X size={18} />
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 gap-3 border-b border-border p-4 sm:grid-cols-2 sm:p-5">
          <div className="overflow-hidden rounded-xl border border-border bg-background/40">
            <div className="border-b border-border/60 px-3 py-2">
              <p className="text-[0.625rem] font-semibold uppercase tracking-wide text-text-muted">
                {currentBucketLabel}
              </p>
              <p className="text-[0.625rem] text-text-muted/80">Bills raised in this period</p>
            </div>
            <SplitRows split={summary?.current} emptyLabel={`Total ${currentBucketLabel}`} />
          </div>
          <div className="overflow-hidden rounded-xl border border-border bg-background/40">
            <div className="border-b border-border/60 px-3 py-2">
              <p className="text-[0.625rem] font-semibold uppercase tracking-wide text-text-muted">
                {oldBucketLabel}
              </p>
              <p className="text-[0.625rem] text-text-muted/80">Bills raised before this period</p>
            </div>
            <SplitRows split={summary?.old} emptyLabel={`Total ${oldBucketLabel}`} />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-2 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div className="flex flex-wrap items-center gap-1.5">
            {bucketOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setBucket(option.value)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  bucket === option.value
                    ? 'bg-primary text-white'
                    : 'border border-border bg-surface text-text-muted hover:text-text'
                }`}
              >
                {option.label}
              </button>
            ))}
            <select
              value={setting}
              onChange={(event) =>
                setSetting(event.target.value as 'all' | 'opd' | 'ipd' | 'pharmacy')
              }
              className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text focus:outline-none focus:ring-2 focus:ring-primary/25"
            >
              <option value="all">All settings</option>
              <option value="ipd">IPD</option>
              <option value="opd">OPD</option>
              <option value="pharmacy">Pharmacy</option>
            </select>
          </div>

          <div className="relative w-full sm:w-72">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search bill, patient or reg. no."
              className="w-full rounded-lg border border-border bg-surface py-1.5 pl-8 pr-3 text-xs text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-auto">
          {error ? (
            <div className="flex items-center gap-2 p-6 text-sm text-danger">
              <AlertCircle size={16} />
              Failed to load due collections.
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center p-10">
              <Loader2 size={22} className="animate-spin text-primary" />
            </div>
          ) : entries.length === 0 ? (
            <div className="p-10 text-center text-sm text-text-muted">
              No due collections match these filters.
            </div>
          ) : (
            <table className="w-full min-w-[860px] text-left">
              <thead className="sticky top-0 z-10 bg-surface text-[0.625rem] uppercase tracking-wide text-text-muted">
                <tr className="border-b border-border">
                  <th className="px-4 py-2.5 font-semibold">Collected</th>
                  <th className="px-4 py-2.5 font-semibold">Patient</th>
                  <th className="px-4 py-2.5 font-semibold">Document</th>
                  <th className="px-4 py-2.5 font-semibold">Bill date</th>
                  <th className="px-4 py-2.5 font-semibold">Due type</th>
                  <th className="px-4 py-2.5 font-semibold">Setting</th>
                  <th className="px-4 py-2.5 font-semibold">Method</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {entries.map((entry) => (
                  <tr key={`${entry.source}-${entry.id}`} className="hover:bg-background/60">
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-text">
                      {fmtDate(entry.movedAt)}
                    </td>
                    <td className="max-w-[180px] truncate px-4 py-2.5 text-xs text-text">
                      {entry.patientName || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <span className="block font-medium text-text">{entry.documentNumber || '—'}</span>
                      <span className="text-[0.625rem] text-text-muted">
                        {SOURCE_LABELS[entry.source] || entry.source}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-text-muted">
                      {fmtDate(entry.documentAt)}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[0.625rem] font-medium ${
                          entry.bucket === 'old'
                            ? 'bg-warning/10 text-warning'
                            : 'bg-primary/10 text-primary'
                        }`}
                      >
                        {entry.bucket === 'old' ? oldBucketLabel : currentBucketLabel}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-text">
                      {SETTING_LABELS[entry.setting] || entry.setting}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-text-muted">
                      {METHOD_LABELS[entry.method] || entry.method}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-success">
                      {fmt(entry.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {isFetching && !isLoading && (
            <div className="flex items-center justify-center gap-2 border-t border-border/60 py-2 text-[0.6875rem] text-text-muted">
              <Loader2 size={12} className="animate-spin" /> Refreshing…
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-xs sm:px-5">
            <p className="text-text-muted">
              Filtered total:{' '}
              <span className="font-bold tabular-nums text-success">{fmt(meta?.filteredTotal)}</span>
            </p>
            {outstandingDue !== undefined && (
              <p className="text-text-muted">
                Still outstanding (all time):{' '}
                <span className="font-semibold tabular-nums text-warning">{fmt(outstandingDue)}</span>
              </p>
            )}
          </div>
          {meta && meta.total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={meta.total}
              limit={LIMIT}
              onPageChange={setPage}
              itemName="collections"
            />
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
